import { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

// Authentication is cookie-based — the backend sets a SameSite=Lax httpOnly
// `tt_session` cookie on register / login / reset-password and clears it on
// logout. The browser sends it on every request thanks to `withCredentials`
// in `api`. We keep a tiny `tt_user` JSON in localStorage purely as a paint
// cache so the UI doesn't flash an empty header before /auth/me returns.
const USER_CACHE_KEY = "tt_user";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem(USER_CACHE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  // On mount, validate the session by calling /auth/me. The cookie travels
  // automatically; if it's missing or expired we clear the cached user.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (cancelled) return;
        setUser(data);
        localStorage.setItem(USER_CACHE_KEY, JSON.stringify(data));
      } catch {
        if (cancelled) return;
        setUser(null);
        localStorage.removeItem(USER_CACHE_KEY);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify(data.user));
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify(data.user));
    return data.user;
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(data));
      return data;
    } catch {
      return null;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Even if the network call fails we still want to drop local state.
    }
    setUser(null);
    localStorage.removeItem(USER_CACHE_KEY);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
